import type { Continuity } from '@dissidium/contracts';
import { allocateItem, validateLedger } from '../continuity/ledger.ts';
import { instantiate, itemDefinition, originShape, startingKit } from '../items/catalogue.ts';
import { equip } from '../items/inventory.ts';
import type { Origin, StartingKitId } from '../items/types.ts';
import { learnCapability } from './learning.ts';

/** Origin sets the starting shape - carried gear and drilled capabilities - never a permanent class. */
export function applyOrigin(input: Continuity, personId: string, origin: Origin, serialPrefix: string): Continuity {
  let c = validateLedger(input);
  const person = c.persons[personId];
  if (!person) throw new Error('Unknown person');
  if (person.lifecycle === 'dead') throw new Error(`${person.current.name} is dead and cannot take an origin shape`);
  const shape = originShape(origin);
  const next = validateLedger(c);
  const target = next.persons[personId];
  if (!target) throw new Error('Unknown person');
  target.current.origin = origin;
  c = validateLedger(next);
  shape.templates.forEach((templateId, index) => {
    const definition = itemDefinition(templateId);
    const item = instantiate(definition, `${serialPrefix}-${index + 1}`, personId);
    c = allocateItem(c, item);
    // Consumables are spent with Treat, never worn: equipping a dressing would push the belt knife off the belt.
    if (definition.slot !== null && !definition.consumable) c = equip(c, personId, item.id);
  });
  for (const capabilityId of shape.capabilities) c = learnCapability(c, personId, capabilityId);
  return c;
}

export function applyStartingKit(input: Continuity, personId: string, kitId: StartingKitId, serialPrefix: string): Continuity {
  return applyOrigin(input, personId, startingKit(kitId).origin, serialPrefix);
}
